<template>
  <SidebarTabTemplate :title="$t('sideToolbar.queue')">
    <template #tool-buttons>
      <Button
        v-tooltip.bottom="$t('g.refresh')"
        icon="pi pi-refresh"
        text
        severity="secondary"
        class="refresh-button"
        :loading="queueStore.isLoading"
        @click="refreshQueue"
      />
      <Button
        v-tooltip.bottom="$t(`sideToolbar.queueTab.${imageFit}ImagePreview`)"
        :icon="
          imageFit === 'cover'
            ? 'pi pi-arrow-down-left-and-arrow-up-right-to-center'
            : 'pi pi-arrow-up-right-and-arrow-down-left-from-center'
        "
        text
        severity="secondary"
        class="toggle-expanded-button"
        @click="toggleImageFit"
      />
      <Button
        v-if="isInFolderView"
        v-tooltip.bottom="$t('sideToolbar.queueTab.backToAllTasks')"
        icon="pi pi-arrow-left"
        text
        severity="secondary"
        class="back-button"
        @click="exitFolderView"
      />
      <template v-else>
        <Button
          v-tooltip="$t('sideToolbar.queueTab.showFlatList')"
          :icon="isExpanded ? 'pi pi-images' : 'pi pi-image'"
          text
          severity="secondary"
          class="toggle-expanded-button"
          @click="toggleExpanded"
        />
        <Button
          v-if="queueStore.hasPendingTasks"
          v-tooltip.bottom="$t('sideToolbar.queueTab.clearPendingTasks')"
          icon="pi pi-stop"
          severity="danger"
          text
          @click="() => commandStore.execute('Comfy.ClearPendingTasks')"
        />
        <Button
          icon="pi pi-trash"
          text
          severity="primary"
          class="clear-all-button"
          @click="confirmRemoveAll($event)"
        />
      </template>
    </template>
    <template #body>
      <div class="queue-body-wrapper">
        <VirtualGrid
          v-if="allTasks?.length"
          :items="allTasks"
          :grid-style="{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            padding: '0.5rem',
            gap: '0.5rem'
          }"
          @approach-end="handleApproachEnd"
        >
          <template #item="{ item }">
            <TaskItem
              :task="item"
              :is-flat-task="isExpanded || isInFolderView"
              @contextmenu="handleContextMenu"
              @preview="handlePreview"
              @task-output-length-clicked="enterFolderView($event)"
            />
          </template>
        </VirtualGrid>
        <!-- 加载状态 -->
        <div v-else-if="queueStore.isLoading" class="loading-container">
          <ProgressSpinner style="width: 50px; height: 50px" />
        </div>
        <!-- 无结果提示 -->
        <div v-else class="no-results-container">
          <NoResultsPlaceholder
            icon="pi pi-info-circle"
            :title="$t('g.noTasksFound')"
            :message="$t('g.noTasksFoundMessage')"
          />
        </div>
        <!-- 加载更多状态，在 VirtualGrid 外部但在同一个滚动容器内 -->
        <div
          v-if="queueStore.isLoadingMore && allTasks?.length"
          class="load-more-indicator"
        >
          <ProgressSpinner style="width: 30px; height: 30px" />
          <span class="load-more-text">{{ $t('g.loading') }}</span>
        </div>
        <!-- 没有更多数据提示 -->
        <div
          v-else-if="
            !queueStore.hasMoreHistory && allTasks?.length && !isInFolderView
          "
          class="no-more-indicator"
        >
          <span class="no-more-text">{{ $t('g.noMoreRecords') }}</span>
        </div>
      </div>
    </template>
  </SidebarTabTemplate>
  <ConfirmPopup />
  <ContextMenu ref="menu" :model="menuItems" />
  <ResultGallery
    v-model:active-index="galleryActiveIndex"
    :all-gallery-items="allGalleryItems"
  />

  <!-- 分享弹窗 -->
  <ShareEditDialog
    v-model:visible="showShareDialog"
    :is-edit-mode="isEditMode"
    :initial-data="shareFormData"
    @cancel="showShareDialog = false"
    @submit="handleShareSubmit"
  />
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import ConfirmPopup from 'primevue/confirmpopup'
import ContextMenu from 'primevue/contextmenu'
import type { MenuItem } from 'primevue/menuitem'
import ProgressSpinner from 'primevue/progressspinner'
import { useConfirm } from 'primevue/useconfirm'
import { useToast } from 'primevue/usetoast'
import { computed, onMounted, ref, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import NoResultsPlaceholder from '@/components/common/NoResultsPlaceholder.vue'
import ShareEditDialog from '@/components/common/ShareEditDialog.vue'
import VirtualGrid from '@/components/common/VirtualGrid.vue'
import { useSettingStore } from '@/platform/settings/settingStore'
import type { ComfyNode } from '@/platform/workflow/validation/schemas/workflowSchema'
import { api } from '@/scripts/api'
import { app } from '@/scripts/app'
import { useLitegraphService } from '@/services/litegraphService'
import { useCommandStore } from '@/stores/commandStore'
import type { ResultItemImpl, TaskItemImpl } from '@/stores/queueStore'
import { useQueueStore } from '@/stores/queueStore'

import SidebarTabTemplate from './SidebarTabTemplate.vue'
import ResultGallery from './queue/ResultGallery.vue'
import TaskItem from './queue/TaskItem.vue'

const IMAGE_FIT = 'Comfy.Queue.ImageFit'
const confirm = useConfirm()
const toast = useToast()
const queueStore = useQueueStore()
const settingStore = useSettingStore()
const commandStore = useCommandStore()
const { t } = useI18n()

// Expanded view: show all outputs in a flat list.
const isExpanded = ref(false)
const galleryActiveIndex = ref(-1)
const allGalleryItems = shallowRef<ResultItemImpl[]>([])
// Folder view: only show outputs from a single selected task.
const folderTask = ref<TaskItemImpl | null>(null)
const isInFolderView = computed(() => folderTask.value !== null)
const imageFit = computed<string>(() => settingStore.get(IMAGE_FIT))

// 分享弹窗相关
const showShareDialog = ref(false)
const shareFormData = ref({
  title: '',
  description: '',
  tag_ids: [] as number[],
  is_public: true
})
const currentShareTask = ref<TaskItemImpl | null>(null)
const currentShareImageUrl = ref('')
const isEditMode = ref(false)

const allTasks = computed(() =>
  isInFolderView.value
    ? folderTask.value
      ? folderTask.value.flatten()
      : []
    : isExpanded.value
      ? queueStore.flatTasks
      : queueStore.tasks
)
const updateGalleryItems = () => {
  allGalleryItems.value = allTasks.value.flatMap((task: TaskItemImpl) => {
    const previewOutput = task.previewOutput
    return previewOutput ? [previewOutput] : []
  })
}

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
}

const refreshQueue = async () => {
  try {
    await queueStore.update(true) // 刷新时使用初始加载
    toast.add({
      severity: 'success',
      summary: t('g.refresh'),
      detail: t('sideToolbar.queueTab.queueRefreshed'),
      life: 2000
    })
  } catch (error) {
    console.error('Failed to refresh queue:', error)
    toast.add({
      severity: 'error',
      summary: t('g.error'),
      detail: t('sideToolbar.queueTab.refreshFailed'),
      life: 3000
    })
  }
}

const handleApproachEnd = async () => {
  // 只在历史记录视图且还有更多数据时加载
  if (
    !isInFolderView.value &&
    !queueStore.isLoadingMore &&
    queueStore.hasMoreHistory
  ) {
    await queueStore.loadMoreHistory()
  }
}

const removeTask = async (task: TaskItemImpl) => {
  if (task.isRunning) {
    await api.interrupt(task.promptId)
  }
  await queueStore.delete(task)
}

const removeAllTasks = async () => {
  await queueStore.clear()
}

const confirmRemoveAll = (event: Event) => {
  confirm.require({
    target: event.currentTarget as HTMLElement,
    message: 'Do you want to delete all tasks?',
    icon: 'pi pi-info-circle',
    rejectProps: {
      label: 'Cancel',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Delete',
      severity: 'danger'
    },
    accept: async () => {
      await removeAllTasks()
      toast.add({
        severity: 'info',
        summary: 'Confirmed',
        detail: 'Tasks deleted',
        life: 3000
      })
    }
  })
}

const menu = ref<InstanceType<typeof ContextMenu> | null>(null)
const menuTargetTask = ref<TaskItemImpl | null>(null)
const menuTargetNode = ref<ComfyNode | null>(null)
const menuItems = computed<MenuItem[]>(() => {
  const items: MenuItem[] = [
    {
      label: t('g.delete'),
      icon: 'pi pi-trash',
      command: () => menuTargetTask.value && removeTask(menuTargetTask.value),
      disabled: isExpanded.value || isInFolderView.value
    },
    {
      label: t('g.loadWorkflow'),
      icon: 'pi pi-file-export',
      command: () => menuTargetTask.value?.loadWorkflow(app),
      disabled: !menuTargetTask.value?.workflow
    },
    {
      label: t('g.goToNode'),
      icon: 'pi pi-arrow-circle-right',
      command: () => {
        if (!menuTargetNode.value) return
        useLitegraphService().goToNode(menuTargetNode.value.id)
      },
      visible: !!menuTargetNode.value
    }
  ]

  if (
    menuTargetTask.value?.previewOutput?.isImage ||
    menuTargetTask.value?.previewOutput?.isVideo
  ) {
    // 添加下载清理图片选项
    // if (menuTargetTask.value?.previewOutput?.isImage) {
    //   items.push({
    //     label: t('g.downloadCleanImage'),
    //     icon: 'pi pi-download',
    //     command: () => {
    //       const task = menuTargetTask.value
    //       if (task?.previewOutput?.url) {
    //         void handleDownloadCleanImage(task)
    //       }
    //     }
    //   })
    // }

    // 暂时显示发布选项，点击时再检查状态
    items.push({
      label: t('g.publishShare'),
      icon: 'pi pi-share-alt',
      command: () => {
        const url = menuTargetTask.value?.previewOutput?.url
        const task = menuTargetTask.value
        if (url && task) {
          void handlePublishShare(task, url)
        }
      }
    })
  }

  return items
})

const handleContextMenu = ({
  task,
  event,
  node
}: {
  task: TaskItemImpl
  event: Event
  node: ComfyNode | null
}) => {
  menuTargetTask.value = task
  menuTargetNode.value = node
  menu.value?.show(event)
}

const handlePreview = (task: TaskItemImpl) => {
  updateGalleryItems()
  galleryActiveIndex.value = allGalleryItems.value.findIndex(
    (item) => item.url === task.previewOutput?.url
  )
}

const enterFolderView = (task: TaskItemImpl) => {
  folderTask.value = task
}

const exitFolderView = () => {
  folderTask.value = null
}

const toggleImageFit = async () => {
  await settingStore.set(
    IMAGE_FIT,
    imageFit.value === 'cover' ? 'contain' : 'cover'
  )
}

// 下载清理后的图片（无工作流数据）
// @ts-expect-error - Reserved for future use

const handleDownloadCleanImage = async (task: TaskItemImpl) => {
  try {
    const imageUrl = task.previewOutput?.url
    if (!imageUrl) {
      console.error('No image URL found for task')
      return
    }

    // 调用API下载清理后的图片
    const blob = await api.downloadCleanImage(imageUrl)

    // 从URL中提取文件名
    const url = new URL(imageUrl)
    const filename = url.searchParams.get('filename') || 'image.png'

    // 创建下载链接
    const downloadUrl = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = `clean_${filename}`

    // 触发下载
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // 清理URL对象
    URL.revokeObjectURL(downloadUrl)
  } catch (error) {
    console.error('Failed to download clean image:', error)
  }
}

// 检查图片是否已分享
const checkShareStatus = async (task: TaskItemImpl): Promise<any> => {
  try {
    const promptId = task.promptId
    if (!promptId) {
      return null
    }

    // 使用合并接口直接查询分享状态
    const shareResponse = await api.fetchApi(`/shares/works/by-prompt-image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        prompt_id: promptId,
        image_url: task.previewOutput?.url
      })
    })

    if (shareResponse.ok) {
      const shareData = await shareResponse.json()
      return shareData
    }

    return null
  } catch (error) {
    console.error('检查分享状态失败:', error)
    return null
  }
}

// 取消发布分享
const handleUnpublishShare = async (task: TaskItemImpl, imageUrl: string) => {
  try {
    // 获取任务的promptId
    const promptId = task.promptId
    if (!promptId) {
      toast.add({
        severity: 'error',
        summary: t('g.error'),
        detail: t('g.noWorkflowExecutionId'),
        life: 3000
      })
      return
    }

    // 使用合并接口直接删除分享
    const response = await api.fetchApi(`/shares/works/by-prompt-image`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        prompt_id: promptId,
        image_url: imageUrl
      })
    })

    if (response.ok) {
      toast.add({
        severity: 'success',
        summary: t('g.success'),
        detail: t('g.shareUnpublishedSuccessfully'),
        life: 3000
      })
    }
  } catch (error) {
    console.error('取消分享失败:', error)
    toast.add({
      severity: 'error',
      summary: t('g.error'),
      detail: t('g.shareUnpublishFailed'),
      life: 3000
    })
  }
}

const handlePublishShare = async (task: TaskItemImpl, imageUrl: string) => {
  // 先检查是否已分享
  const shareInfo = await checkShareStatus(task)

  if (shareInfo) {
    // 已分享，显示修改和取消选项的确认弹窗
    const result = await new Promise((resolve) => {
      // 这里可以显示一个选择弹窗，让用户选择修改还是取消
      // 暂时直接进入编辑模式
      resolve('edit')
    })

    if (result === 'edit') {
      void handleEditShare(task, imageUrl, shareInfo)
      return
    } else if (result === 'delete') {
      void handleUnpublishShare(task, imageUrl)
      return
    }
  }

  // 未分享，显示新建分享弹窗
  showShareDialog.value = true
  shareFormData.value = {
    title: '',
    description: '',
    tag_ids: [],
    is_public: true
  }
  currentShareTask.value = task
  currentShareImageUrl.value = imageUrl
  isEditMode.value = false
}

// 修改分享
const handleEditShare = async (
  task: TaskItemImpl,
  imageUrl: string,
  shareInfo: any
) => {
  // 显示带信息的分享弹窗
  showShareDialog.value = true
  shareFormData.value = {
    title: shareInfo.title || '',
    description: shareInfo.description || '',
    tag_ids: shareInfo.tags ? shareInfo.tags.map((tag: any) => tag.id) : [],
    is_public: shareInfo.is_public !== false
  }
  currentShareTask.value = task
  currentShareImageUrl.value = imageUrl
  isEditMode.value = true
}

// 处理分享弹窗提交
const handleShareSubmit = async (formData: any) => {
  try {
    if (!currentShareTask.value || !currentShareImageUrl.value) {
      return
    }

    // 获取任务的promptId
    const promptId = currentShareTask.value.promptId
    if (!promptId) {
      toast.add({
        severity: 'error',
        summary: t('g.error'),
        detail: t('g.noWorkflowExecutionId'),
        life: 3000
      })
      return
    }

    let response
    if (isEditMode.value) {
      // 编辑模式，使用合并接口更新分享
      response = await api.fetchApi(`/shares/works/by-prompt-image`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt_id: promptId,
          image_url: currentShareImageUrl.value,
          title: formData.title,
          description: formData.description,
          tag_ids: formData.tag_ids,
          is_public: formData.is_public
        })
      })
    } else {
      // 新建模式，调用创建API（这个接口不需要合并，因为创建时需要prompt_execution_id）
      // 先通过promptId获取promptExecutionId
      const promptExecutionResponse = await api.fetchApi(
        `/prompt/executions/by-prompt-id/${promptId}`,
        {
          method: 'GET'
        }
      )
      const promptExecutionData = await promptExecutionResponse.json()
      if (!promptExecutionData?.id) {
        toast.add({
          severity: 'error',
          summary: t('g.error'),
          detail: t('g.noWorkflowExecutionId'),
          life: 3000
        })
        return
      }

      response = await api.fetchApi('/shares/works', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          prompt_execution_id: promptExecutionData.id,
          image_url: currentShareImageUrl.value,
          title: formData.title,
          description: formData.description,
          tag_ids: formData.tag_ids,
          is_public: formData.is_public
        })
      })
    }

    if (response.ok) {
      toast.add({
        severity: 'success',
        summary: t('g.success'),
        detail: isEditMode.value
          ? t('g.shareUpdatedSuccessfully')
          : t('g.sharePublishedSuccessfully'),
        life: 3000
      })
      showShareDialog.value = false
    }
  } catch (error) {
    console.error('分享失败:', error)

    // 检查是否是重复分享的错误
    let errorMessage = t('g.sharePublishFailed')
    if (
      error &&
      typeof error === 'object' &&
      'message' in error &&
      typeof error.message === 'string' &&
      (error.message.includes('已经分享过了') ||
        error.message.includes('已经分享过'))
    ) {
      errorMessage = t('g.shareAlreadyExists')
    }

    toast.add({
      severity: 'error',
      summary: t('g.error'),
      detail: errorMessage,
      life: 3000
    })
  }
}

watch(allTasks, () => {
  const isGalleryOpen = galleryActiveIndex.value !== -1
  if (!isGalleryOpen) return

  const prevLength = allGalleryItems.value.length
  updateGalleryItems()
  const lengthChange = allGalleryItems.value.length - prevLength
  if (!lengthChange) return

  const newIndex = galleryActiveIndex.value + lengthChange
  galleryActiveIndex.value = Math.max(0, newIndex)
})

// 组件挂载时进行初始加载
onMounted(async () => {
  // 如果还没有加载过数据且不在加载中，进行初始加载
  if (queueStore.historyTasks.length === 0 && !queueStore.isLoading) {
    await queueStore.update(true)
  }
})
</script>

<style scoped>
.queue-body-wrapper {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.queue-body-wrapper :deep(.scroll-container) {
  height: 100%;
  overflow-y: auto;
  flex: 1;
}

.loading-container,
.no-results-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  flex: 1;
  padding: 2rem;
}

.load-more-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  gap: 0.5rem;
}

.load-more-text {
  font-size: 0.875rem;
  color: var(--text-color-secondary, #a3a3a3);
}

.no-more-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.no-more-text {
  font-size: 0.875rem;
  color: var(--text-color-secondary, #a3a3a3);
  text-align: center;
}
</style>
